import type { Phone } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionSubmit,
  Label,
  Modal,
  ModalBody,
  ModalButtons,
  ModalButton,
  ModalCloseX,
  ModalContentForm,
  ModalHeading,
  ValidationMessage,
  ValidationContainer,
  PhoneNumberInput,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useCallback,
  useMemo,
  useState,
} from "react"
import type {
  ReactNode,
  FormEventHandler,
} from "react"
import { isSupportedCountry } from "react-phone-number-input"

import { DiscardChangesDialog } from "../DiscardChangesDialog"
import { emptyPhone } from "./emptyPhone"
import styles from "./PhoneNumberModal.module.scss"
import { PhoneNumberModalContext } from "./PhoneNumberModalContext"

import {
  CountryCodeSelect,
  defaultCountryCode,
} from "src/components/CountryCodeSelect"
import {
  usePolyglot,
  useOverlayLoader,
} from "src/contexts"
import {
  useEditableType,
  useAccountCountry,
} from "src/hooks"

const phoneId = "phone-number-id"
const saveButtonId = "save-phone-id"
const phoneErrorId = "phone-number-error-id"
const polyglotPrefix = "components.phone_modal."

interface Props {
  children: ReactNode;
  onSubmit: (currentPhone: Phone, originalPhone: Phone, closeModal: () => void) => Promise<void>;
}

export function PhoneNumberModal(props: Props) {
  const {
    onSubmit: propsOnSubmit,
    children,
  } = props

  const polyglot = usePolyglot()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const [
    isDiscardOpen,
    setIsDiscardOpen,
  ] = useState<boolean>(false)
  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState<boolean>(false)

  const {
    current: currentPhone,
    original: originalPhone,
    hasChanged: phoneHasChanged,
    save: resetPhone,
    update: editPhone,
  } = useEditableType<Phone>(emptyPhone)

  // force closes the modal
  const closeModal = useCallback(
    () => {
      setIsModalOpen(false)
    },
    [setIsModalOpen],
  )

  // opens the modal and fills it w/ the specified phone
  const openModal = useCallback(
    (phone: Phone = emptyPhone) => {
      resetPhone(phone)
      setIsModalOpen(true)
    },
    [
      setIsModalOpen,
      resetPhone,
    ],
  )

  // show the warning dialog if changes have been made without saving
  const onOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        if (phoneHasChanged) {
          setIsDiscardOpen(true)
        } else {
          setIsModalOpen(false)
        }
      }
    },
    [
      phoneHasChanged,
      setIsModalOpen,
      setIsDiscardOpen,
    ],
  )

  const isNewPhone = currentPhone.id === emptyPhone.id

  const contextValue = useMemo(
    () => ({
      openPhoneNumberModal: openModal,
    }),
    [openModal],
  )

  const country = useAccountCountry()
  const initialCountryCode: string = useMemo(
    () => {
      if (!country?.phone_supported) {
        return defaultCountryCode
      }

      const currentValue = country.country_code

      if (isSupportedCountry(currentValue)) {
        return currentValue
      }

      return defaultCountryCode
    },
    [country],
  )

  const [
    countryCode,
    setCountryCode,
  ] = useState<string>(initialCountryCode)

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setIsOverlayLoaderVisible(true)
        await propsOnSubmit(
          currentPhone,
          originalPhone,
          closeModal,
        )
        setIsOverlayLoaderVisible(false)
      })()
    },
    [
      propsOnSubmit,
      currentPhone,
      originalPhone,
      closeModal,
      setIsOverlayLoaderVisible,
    ],
  )

  const onPhoneChange = useCallback(
    (value?: string) => {
      editPhone({
        ...currentPhone,
        phone_number: value ?? "",
      })
    },
    [
      editPhone,
      currentPhone,
    ],
  )

  return (
    <Modal
      open={isModalOpen}
      onOpenChange={onOpenChange}
    >
      <PhoneNumberModalContext.Provider value={contextValue}>
        {children}
      </PhoneNumberModalContext.Provider>
      <ModalContentForm onSubmit={onSubmit}>
        <ModalHeading>
          {polyglot.t(`${polyglotPrefix}${isNewPhone ? "add_title" : "edit_title"}`)}
          <ModalCloseX />
        </ModalHeading>
        <ModalBody className={styles.modalBody}>
          <div>
            <Label className={styles.label}>
              {polyglot.t(`${polyglotPrefix}country_code.label`)}
            </Label>
            <CountryCodeSelect
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              required
            />
          </div>
          <div>
            <Label className={styles.label}>
              {polyglot.t(`${polyglotPrefix}phone_number.label`)}
            </Label>
            <ValidationContainer>
              <PhoneNumberInput
                id={phoneId}
                className={styles.phoneInput}
                country={countryCode}
                placeholder={polyglot.t(`${polyglotPrefix}phone_number.placeholder`)}
                value={currentPhone.phone_number}
                onChange={onPhoneChange}
                autoComplete="tel"
                required
              />
              <ValidationMessage
                id={phoneErrorId}
                invalidMessage={{
                  customError: polyglot.t("validation_message.phone_invalid"),
                  valueMissing: polyglot.t("validation_message.value_missing"),
                }}
              />
            </ValidationContainer>
          </div>
          <ModalButtons>
            <ModalButton
              stylePreset="secondary"
              type="button"
            >
              {polyglot.t(`${polyglotPrefix}cancel_cta`)}
            </ModalButton>
            <ActionSubmit
              className={styles.modalMultiButton}
              stylePreset="primary"
              value={polyglot.t(`${polyglotPrefix}save_cta`)}
              disabled={!phoneHasChanged}
              id={saveButtonId}
            />
          </ModalButtons>
        </ModalBody>
        <DiscardChangesDialog
          open={isDiscardOpen}
          setOpen={setIsDiscardOpen}
          discardChanges={closeModal}
        />
      </ModalContentForm>
    </Modal>
  )
}
